import { useState } from 'react';

export interface ReviewChecklistItem {
  id: string;
  label: string;
  description?: string;
  required: boolean;
}

interface ReviewGateProps {
  onApprove: () => void;
  onReject: (reason: string) => void;
  approved: boolean;
  loading?: boolean;
}

const DEFAULT_CHECKLIST: ReviewChecklistItem[] = [
  {
    id: 'artwork_quality',
    label: 'Artwork meets quality bar',
    description: 'Resolution, contrast, and aesthetic alignment verified',
    required: true,
  },
  {
    id: 'copy_review',
    label: 'Copy reviewed',
    description: 'Product name and description checked for accuracy',
    required: true,
  },
  {
    id: 'pricing_ok',
    label: 'Pricing verified',
    description: 'Retail price meets minimum margin',
    required: true,
  },
  {
    id: 'variants_complete',
    label: 'Variants complete',
    description: 'All intended sizes and colors configured',
    required: true,
  },
  {
    id: 'mockups_approved',
    label: 'Mockups approved',
    description: 'Mockup images look correct on all garment colors',
    required: false,
  },
];

export function ReviewGate({ onApprove, onReject, approved, loading }: ReviewGateProps) {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const allRequired = DEFAULT_CHECKLIST.filter((c) => c.required).every((c) => checked[c.id]);

  function toggle(id: string) {
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function handleApprove() {
    if (!allRequired) return;
    onApprove();
  }

  function handleReject() {
    onReject(rejectReason || 'Failed review checklist');
    setShowReject(false);
    setRejectReason('');
  }

  if (approved) {
    return (
      <div className="panel" style={{ borderColor: 'var(--green)', textAlign: 'center', padding: '24px' }}>
        <div style={{ fontSize: '1.2rem', color: 'var(--green)', fontWeight: 600, marginBottom: '8px' }}>
          &#10003; Review Approved
        </div>
        <p style={{ color: 'var(--ink-muted)', margin: 0 }}>This job has passed the final review gate.</p>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="panel__header">
        <h3 className="panel__title">Final Review Checklist</h3>
      </div>

      <div style={{ marginBottom: '16px' }}>
        {DEFAULT_CHECKLIST.map((item) => (
          <div key={item.id} className="checkbox-row">
            <input
              type="checkbox"
              id={item.id}
              checked={checked[item.id] ?? false}
              onChange={() => toggle(item.id)}
            />
            <div className="checkbox-row__label">
              <label htmlFor={item.id} style={{ cursor: 'pointer', fontWeight: 500 }}>
                {item.label}
                {item.required && <span style={{ color: 'var(--wine)', marginLeft: '4px' }}>*</span>}
              </label>
              {item.description && (
                <div style={{ fontSize: '0.75rem', color: 'var(--ink-faint)', marginTop: '2px' }}>
                  {item.description}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {showReject ? (
        <div style={{ marginTop: '16px' }}>
          <div className="form-group">
            <label className="form-label">Rejection Reason</label>
            <textarea
              className="form-textarea"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explain why this job is being rejected..."
              rows={3}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn--danger" onClick={handleReject} disabled={loading}>
              Confirm Rejection
            </button>
            <button className="btn btn--ghost" onClick={() => setShowReject(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="btn btn--primary"
            onClick={handleApprove}
            disabled={!allRequired || loading}
          >
            {loading ? 'Processing...' : 'Approve & Continue'}
          </button>
          <button className="btn btn--danger" onClick={() => setShowReject(true)} disabled={loading}>
            Reject
          </button>
        </div>
      )}

      {!allRequired && (
        <div className="form-hint" style={{ marginTop: '8px' }}>
          All required items must be checked to approve.
        </div>
      )}
    </div>
  );
}
